import { useQuery } from '@tanstack/react-query';
import { endpoints } from '@/lib/api';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { StatCard } from '@/components/ui/StatCard';
import { StatusBadge, StatusIcon } from '@/components/ui/StatusBadge';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { Sparkline } from '@/components/ui/Sparkline';
import { Cpu, MemoryStick, HardDrive, Server, Box } from 'lucide-react';
import { Link } from 'wouter';
import { formatBytes, formatPercent, formatUptime } from '@/lib/format';
import { SensorsWidget } from '@/components/SensorsWidget';
import { BackupsWidget } from '@/components/BackupsWidget';
import type { ProxmoxContainer, ProxmoxVm } from '@/lib/api';

const REFETCH = 5_000;

export function ProxmoxRoute() {
  const nodeQ = useQuery({
    queryKey: ['proxmox', 'node'],
    queryFn: endpoints.proxmox.node,
    refetchInterval: REFETCH,
  });
  const lxcQ = useQuery({
    queryKey: ['proxmox', 'lxc'],
    queryFn: endpoints.proxmox.lxc,
    refetchInterval: REFETCH,
  });
  const qemuQ = useQuery({
    queryKey: ['proxmox', 'qemu'],
    queryFn: endpoints.proxmox.qemu,
    refetchInterval: REFETCH,
  });
  const storageQ = useQuery({
    queryKey: ['proxmox', 'storage'],
    queryFn: endpoints.proxmox.storage,
    refetchInterval: REFETCH,
  });
  const metricsQ = useQuery({
    queryKey: ['proxmox', 'metrics', '1h'],
    queryFn: () => endpoints.proxmox.metrics('1h'),
    refetchInterval: REFETCH,
  });

  const node = nodeQ.data;
  const containers = lxcQ.data?.containers ?? [];
  const vms = qemuQ.data?.vms ?? [];
  const storage = storageQ.data?.storage ?? [];
  const points = metricsQ.data?.points ?? [];

  const cpuSeries = points.slice(-60).map((p) => p.cpu);
  const ramSeries = points
    .slice(-60)
    .map((p) => (p.ram_total > 0 ? (p.ram_used / p.ram_total) * 100 : 0));
  const loadSeries = points.slice(-60).map((p) => p.load_average);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Proxmox VE</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Hypervisor health, containers, VMs and storage · {node?.pveVersion ?? 'connecting…'}
          </p>
        </div>
        {node && (
          <StatusBadge status={node.online ? 'online' : 'offline'} label={node.online ? 'online' : 'offline'} />
        )}
      </div>

      {/* Host stat row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          label="CPU"
          value={node?.cpu ? `${node.cpu.percent.toFixed(1)}%` : '—'}
          icon={Cpu}
          hint={node?.cpu ? `${node.cpu.cores} cores · load ${node.cpu.loadAverage.toFixed(2)}` : undefined}
          tone={
            !node?.cpu
              ? 'default'
              : node.cpu.percent < 60
                ? 'success'
                : node.cpu.percent < 85
                  ? 'warn'
                  : 'danger'
          }
        />
        <StatCard
          label="Memory"
          value={node?.memory ? formatPercent(node.memory.percent) : '—'}
          icon={MemoryStick}
          hint={node?.memory ? `${formatBytes(node.memory.used)} / ${formatBytes(node.memory.total)}` : undefined}
        />
        <StatCard
          label="Root FS"
          value={node?.rootfs ? formatPercent(node.rootfs.percent) : '—'}
          icon={HardDrive}
          hint={node?.rootfs ? `${formatBytes(node.rootfs.used)} / ${formatBytes(node.rootfs.total)}` : undefined}
        />
        <StatCard
          label="Uptime"
          value={node?.uptime ? formatUptime(node.uptime) : '—'}
          icon={Server}
          hint={node?.kernel ? node.kernel.split(' ')[0] : undefined}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card>
          <CardHeader>
            <CardTitle>CPU %</CardTitle>
            <span className="text-xs tabular-nums text-muted-foreground">
              {cpuSeries.length > 0 ? `${cpuSeries[cpuSeries.length - 1]?.toFixed(1)}%` : '—'}
            </span>
          </CardHeader>
          <CardBody className="h-[120px] flex items-center">
            <Sparkline
              values={cpuSeries}
              width={520}
              height={100}
              className="w-full"
            />
          </CardBody>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>RAM %</CardTitle>
            <span className="text-xs tabular-nums text-muted-foreground">
              {ramSeries.length > 0 ? `${ramSeries[ramSeries.length - 1]?.toFixed(1)}%` : '—'}
            </span>
          </CardHeader>
          <CardBody className="h-[120px] flex items-center">
            <Sparkline values={ramSeries} width={520} height={100} domain={[0, 100]} className="w-full" />
          </CardBody>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Load avg</CardTitle>
            <span className="text-xs tabular-nums text-muted-foreground">
              {loadSeries.length > 0 ? loadSeries[loadSeries.length - 1]?.toFixed(2) : '—'}
            </span>
          </CardHeader>
          <CardBody className="h-[120px] flex items-center">
            <Sparkline values={loadSeries} width={520} height={100} className="w-full" />
          </CardBody>
        </Card>
      </div>

      {/* Sensors */}
      <SensorsWidget />

      {/* Containers */}
      <Card>
        <CardHeader>
          <CardTitle>Containers ({containers.length})</CardTitle>
          <span className="text-xs text-muted-foreground">
            {containers.filter((c) => c.status === 'running').length} running
          </span>
        </CardHeader>
        <CardBody className="p-0">
          {containers.length === 0 ? (
            <Empty msg="no containers yet" />
          ) : (
            <div className="divide-y divide-border">
              {containers
                .slice()
                .sort((a, b) => a.vmid - b.vmid)
                .map((c) => <ContainerRow key={c.vmid} c={c} />)}
            </div>
          )}
        </CardBody>
      </Card>

      {/* VMs */}
      <Card>
        <CardHeader>
          <CardTitle>Virtual machines ({vms.length})</CardTitle>
          <span className="text-xs text-muted-foreground">
            {vms.filter((v) => v.status === 'running').length} running
          </span>
        </CardHeader>
        <CardBody className="p-0">
          {vms.length === 0 ? (
            <Empty msg="no VMs" />
          ) : (
            <div className="divide-y divide-border">
              {vms
                .slice()
                .sort((a, b) => a.vmid - b.vmid)
                .map((vm) => (
                  <VmRow key={vm.vmid} vm={vm} />
                ))}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Backups */}
      <BackupsWidget />

      {/* Storage */}
      <Card>
        <CardHeader>
          <CardTitle>Storage</CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {storage.length === 0 ? (
            <Empty msg="no storage pools" />
          ) : (
            <div className="divide-y divide-border">
              {storage.map((s) => (
                <div key={s.storage} className="px-4 py-3 grid grid-cols-12 gap-3 items-center">
                  <div className="col-span-3">
                    <div className="font-medium">{s.storage}</div>
                    <div className="text-xs text-muted-foreground">{s.type}</div>
                  </div>
                  <div className="col-span-6">
                    <ProgressBar value={s.percent} />
                  </div>
                  <div className="col-span-3 text-right text-sm tabular-nums">
                    {formatPercent(s.percent)}
                    <div className="text-xs text-muted-foreground">
                      {formatBytes(s.used)} / {formatBytes(s.total)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function ContainerRow({ c }: { c: ProxmoxContainer }) {
  const memPct = c.memTotal > 0 ? (c.memUsed / c.memTotal) * 100 : 0;
  return (
    <Link href={`/proxmox/lxc/${c.vmid}`}>
      <a className="block">
        <div className="px-4 py-3 grid grid-cols-12 gap-3 items-center hover:bg-accent/30 transition-colors">
          <div className="col-span-1 text-xs text-muted-foreground font-mono">{c.vmid}</div>
          <div className="col-span-3 flex items-center gap-2">
            <StatusIcon status={c.status} />
            <span className="font-medium truncate">{c.name}</span>
          </div>
          <div className="col-span-2">
            <StatusBadge status={c.status} />
          </div>
          <div className="col-span-2 text-sm tabular-nums">
            <div className="text-xs text-muted-foreground">CPU</div>
            {c.cpu.toFixed(1)}%
          </div>
          <div className="col-span-3">
            <div className="flex justify-between text-xs mb-1">
              <span className="text-muted-foreground">RAM</span>
              <span className="tabular-nums">{formatPercent(memPct)}</span>
            </div>
            <ProgressBar value={memPct} />
          </div>
          <div className="col-span-1 text-xs text-muted-foreground text-right">
            {c.status === 'running' ? formatUptime(c.uptime) : '—'}
          </div>
        </div>
      </a>
    </Link>
  );
}

function VmRow({ vm }: { vm: ProxmoxVm }) {
  const memPct = vm.memTotal > 0 ? (vm.memUsed / vm.memTotal) * 100 : 0;
  return (
    <div className="px-4 py-3 grid grid-cols-12 gap-3 items-center hover:bg-accent/30 transition-colors">
      <div className="col-span-1 text-xs text-muted-foreground font-mono">{vm.vmid}</div>
      <div className="col-span-3 flex items-center gap-2">
        <Box className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="font-medium truncate">{vm.name}</span>
      </div>
      <div className="col-span-2">
        <StatusBadge status={vm.status} />
      </div>
      <div className="col-span-2 text-sm tabular-nums">
        <div className="text-xs text-muted-foreground">CPU</div>
        {vm.cpu.toFixed(1)}%
      </div>
      <div className="col-span-3">
        <div className="flex justify-between text-xs mb-1">
          <span className="text-muted-foreground">RAM</span>
          <span className="tabular-nums">{formatPercent(memPct)}</span>
        </div>
        <ProgressBar value={memPct} />
      </div>
      <div className="col-span-1 text-xs text-muted-foreground text-right">
        {vm.status === 'running' ? formatUptime(vm.uptime) : '—'}
      </div>
    </div>
  );
}

function Empty({ msg }: { msg: string }) {
  return <div className="p-8 text-center text-sm text-muted-foreground">{msg}</div>;
}
